/* SPEC-146 — the Rulings Desk modal.
 *
 * A login-time wizard that walks Sudhin through the open governance decisions one at a
 * time: option cards + free text + a "talk it through" hand-off to the existing copilot.
 * Same visual family as the tour picker (SPEC-145). The iron rule, stated on screen:
 * nothing changes until Param applies the answer — an answer here is a recorded ruling,
 * never a behaviour change. */
import { useEffect, useRef, useState } from 'react';
import {
  X, ChevronLeft, ChevronRight, MessageCircleQuestion, Check, Gavel, Users, UserRound, Clock, Gauge,
  AlertTriangle, Forward,
} from 'lucide-react';
import { RulingCard, RulingPerson, fetchRulingsPeople } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useRulings } from './RulingsProvider';

const APPLIED_NOTE =
  'Nothing changes on screen until Param applies your answer and confirms it back to you here.';

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Peer answers carry a naive ISO timestamp; render a short, human date ("31 Jul") when parseable.
function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

// Whole days between an answer-by date and today (>= 0 when overdue). Local-date based.
function daysOverdue(answerBy: string | null | undefined): number {
  if (!answerBy) return 0;
  const due = new Date(answerBy);
  if (isNaN(due.getTime())) return 0;
  const ms = Date.now() - due.getTime();
  return Math.max(0, Math.floor(ms / 86400000));
}

export function RulingsDesk() {
  const { pending, mine, deskOpen, closeDesk, snooze, talkThrough, answer, recordProvisional, reroute, error: loadError, loading, refresh } = useRulings();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // SPEC-327 admin provisional-decision form (per card).
  const [provOpen, setProvOpen] = useState<Record<string, boolean>>({});
  const [provDecision, setProvDecision] = useState<Record<string, string>>({});
  const [provRationale, setProvRationale] = useState<Record<string, string>>({});
  const [provSubmitting, setProvSubmitting] = useState(false);

  // Snapshot the queue when the desk opens so answering (which refetches pending) doesn't
  // reshuffle the wizard under the user's feet.
  const [queue, setQueue] = useState<RulingCard[]>([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [freeText, setFreeText] = useState<Record<string, string>>({});
  // SPEC-224: gap_ids where the user chose "I see it differently" on a peer-answered card,
  // revealing the normal option/free-text flow instead of the one-tap agree.
  const [differ, setDiffer] = useState<Record<string, boolean>>({});
  const [recorded, setRecorded] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRecent, setShowRecent] = useState(false);
  // SPEC-584 — "Not my decision": per-card panel state, the people list (loaded once, when a
  // panel first opens), and the cards passed on this session (they leave the queue like an
  // answered card; the server has already moved them to the new owner's desk).
  const [rrOpen, setRrOpen] = useState<Record<string, boolean>>({});
  const [rrTo, setRrTo] = useState<Record<string, string>>({});
  const [rrNote, setRrNote] = useState<Record<string, string>>({});
  const [rrKeep, setRrKeep] = useState<Record<string, boolean>>({});
  const [passedOn, setPassedOn] = useState<Record<string, string>>({});
  const [people, setPeople] = useState<RulingPerson[] | null>(null);
  const [peopleError, setPeopleError] = useState(false);
  const [rrSubmitting, setRrSubmitting] = useState(false);
  const rrSelectRef = useRef<HTMLSelectElement>(null);
  // Escape closes an open "Not my decision" panel before it closes the desk (review finding 12).
  const rrOpenRef = useRef<Record<string, boolean>>({});
  rrOpenRef.current = rrOpen;
  const rrButtonRef = useRef<HTMLButtonElement>(null);
  const rrRefocusButton = useRef(false);
  const wasOpen = useRef(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // SPEC-462: also take the snapshot when an EMPTY queue gets cards while open — that is
    // the desk's "Try again" after a failed load succeeding. A non-empty queue is never
    // re-snapshotted, so answering still cannot reshuffle the wizard.
    if (deskOpen && (!wasOpen.current || (queue.length === 0 && pending.length > 0))) {
      setQueue(pending);
      setIdx(0);
      setError(null);
      setRecorded(new Set());
      setDiffer({});
      setPassedOn({});
      setRrOpen({});
    }
    wasOpen.current = deskOpen;
  }, [deskOpen, pending]);

  useEffect(() => {
    if (!deskOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const openPanels = Object.keys(rrOpenRef.current).filter((k) => rrOpenRef.current[k]);
      if (openPanels.length > 0) {
        rrRefocusButton.current = true;
        setRrOpen({});
        return;
      }
      closeDesk();
    };
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [deskOpen, closeDesk]);

  // SPEC-584 review: move focus into the "Not my decision" panel once its picker is usable
  // (a disabled select cannot take focus), and back to the button when the panel closes.
  const currentGap = queue[idx]?.gap_id;
  const panelOpen = !!(currentGap && rrOpen[currentGap]);
  useEffect(() => {
    if (panelOpen && people !== null) rrSelectRef.current?.focus();
    if (!panelOpen && rrRefocusButton.current) {
      rrRefocusButton.current = false;
      rrButtonRef.current?.focus();
    }
  }, [panelOpen, people]);

  if (!deskOpen) return null;

  const total = queue.length;
  // A failed load leaves `pending` empty — say so rather than claim nothing is waiting.
  const loadFailed = loadError && total === 0;
  // A (re)load in flight with nothing snapshotted is NOT "nothing waiting" either — the
  // retry clears the error first, so without this the empty copy flashed back (delta
  // review finding 2).
  const reloading = !loadFailed && loading && total === 0;
  const card: RulingCard | undefined = queue[idx];
  // A card is done once answered or passed on (SPEC-584).
  const isDone = (gapId: string) => recorded.has(gapId) || gapId in passedOn;
  const allRecorded = total > 0 && queue.every((c) => isDone(c.gap_id));

  const goNextUnrecorded = () => {
    for (let step = 1; step <= total; step++) {
      const j = (idx + step) % total;
      if (!isDone(queue[j].gap_id)) {
        setIdx(j);
        return;
      }
    }
  };

  const onSubmit = async () => {
    if (!card) return;
    const opt = selected[card.gap_id] || null;
    const txt = (freeText[card.gap_id] || '').trim() || null;
    if (!opt && !txt) return;
    setSubmitting(true);
    setError(null);
    try {
      // An answer through the normal flow is the user's own view. On a peer-answered card
      // this is the "I see it differently" path — recorded as a non-concurrence (SPEC-224).
      await answer(card.gap_id, { option_key: opt, answer_text: txt, concurrence: false });
      setRecorded((prev) => new Set(prev).add(card.gap_id));
    } catch (e: any) {
      setError(e?.message || 'Could not record your answer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // SPEC-224: "I agree" — one tap, files a concurrence echoing the peer's option so triage
  // reads the consolidation directly. Free-text-only peers carry a null option; the backend
  // records the agreement on the concurrence flag alone.
  const onAgree = async () => {
    if (!card || !card.peer_answer) return;
    setSubmitting(true);
    setError(null);
    try {
      await answer(card.gap_id, {
        option_key: card.peer_answer.option_key,
        answer_text: null,
        concurrence: true,
      });
      setRecorded((prev) => new Set(prev).add(card.gap_id));
    } catch (e: any) {
      setError(e?.message || 'Could not record your answer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // SPEC-327 — admin records a provisional decision on an overdue card. The suggestion is
  // pre-filled from what the system runs today (the interim default); Param edits before
  // recording. It changes nothing and the owner can still revise by answering.
  const onRecordProvisional = async () => {
    if (!card) return;
    const decision = (provDecision[card.gap_id] || '').trim();
    if (!decision) return;
    setProvSubmitting(true);
    setError(null);
    try {
      await recordProvisional(card.gap_id, decision, (provRationale[card.gap_id] || '').trim() || undefined);
      setProvOpen((s) => ({ ...s, [card.gap_id]: false }));
    } catch (e: any) {
      setError(e?.message || 'Could not record the provisional decision.');
    } finally {
      setProvSubmitting(false);
    }
  };

  // SPEC-584 — open the "Not my decision" panel; the suggested owner is preselected.
  const openReroute = async () => {
    if (!card) return;
    setRrOpen((s) => ({ ...s, [card.gap_id]: true }));
    setRrTo((s) => ({ ...s, [card.gap_id]: s[card.gap_id] ?? (card.reroute_suggestion?.username || '') }));
    // A failed load leaves `people` null, so opening the panel again retries it.
    if (people === null) {
      try {
        const r = await fetchRulingsPeople();
        setPeople(r.people || []);
        setPeopleError(false);
      } catch {
        setPeopleError(true);
      }
    }
  };

  const onReroute = async () => {
    if (!card) return;
    const to = rrTo[card.gap_id];
    if (!to) return;
    setRrSubmitting(true);
    setError(null);
    try {
      await reroute(card.gap_id, to, (rrNote[card.gap_id] || '').trim() || undefined, !!rrKeep[card.gap_id]);
      const name = people?.find((p) => p.username === to)?.display_name || cap(to);
      setPassedOn((s) => ({ ...s, [card.gap_id]: name }));
      setRrOpen((s) => ({ ...s, [card.gap_id]: false }));
    } catch (e: any) {
      setError(e?.message || 'Could not pass this decision on. Please try again.');
    } finally {
      setRrSubmitting(false);
    }
  };

  const passedTo = card ? passedOn[card.gap_id] : undefined;
  const isRecorded = card ? recorded.has(card.gap_id) || passedTo !== undefined : false;
  const peer = card?.peer_answer;
  // While a peer has answered and the user hasn't yet chosen to differ (or recorded), the
  // wizard shows the one-tap agree/differ choice instead of the raw option list.
  const inChooseMode = !!peer && !differ[card!.gap_id] && !isRecorded;
  const canSubmit =
    !!card && !submitting && (!!selected[card.gap_id] || (freeText[card.gap_id] || '').trim().length > 0);

  return (
    <div className="fixed inset-0 z-[80] flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div className="fixed inset-0 bg-black/60" onClick={closeDesk} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="rulings-desk-title"
        className="relative w-full max-w-2xl my-8 bg-[var(--bg-raised)] border border-[var(--border-subtle)] rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-[var(--border-subtle)]">
          <div className="min-w-0">
            <h2
              id="rulings-desk-title"
              className="flex items-center gap-2 text-base font-display font-bold text-[var(--text-primary)]"
            >
              <Gavel className="w-4 h-4 text-amber-400 shrink-0" />
              {total > 0
                ? `${total} decision${total === 1 ? '' : 's'} ${total === 1 ? 'is' : 'are'} waiting for your steer`
                : loadFailed
                  ? 'Could not load your decisions'
                  : reloading
                    ? 'Loading your decisions…'
                    : 'No decisions waiting right now'}
            </h2>
            <p className="text-xs text-[var(--text-tertiary)] mt-1 leading-snug">
              These are the calls only you can make. {APPLIED_NOTE}
            </p>
          </div>
          <button
            ref={closeRef}
            onClick={closeDesk}
            className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          {/* Waiting-for-you wizard */}
          {/* SPEC-462 review finding 1: a failed load is NOT "nothing waiting". The rail's
              Decisions button shows "!" on an error and promises a retry, so the desk says
              what happened and offers one, instead of the empty-state copy. */}
          {loadFailed ? (
            <div className="px-6 py-10 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              </div>
              <p className="mt-3 text-sm font-semibold text-[var(--text-primary)]">
                Your decisions could not be loaded just now.
              </p>
              <p className="mt-1 text-xs text-[var(--text-tertiary)]">
                This is a loading problem, not an empty list — there may be decisions waiting.
              </p>
              <button
                type="button"
                onClick={refresh}
                className="mt-4 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500 text-[var(--text-inverse)] hover:bg-amber-400 transition-colors"
              >
                Try again
              </button>
            </div>
          ) : reloading ? (
            <div className="px-6 py-10 text-center" role="status">
              <p className="text-sm text-[var(--text-secondary)]">Loading your decisions…</p>
            </div>
          ) : allRecorded || !card ? (
            <div className="px-6 py-10 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-green-500/10 border border-green-500/25 flex items-center justify-center">
                <Check className="w-6 h-6 text-green-400" />
              </div>
              <p className="mt-3 text-sm font-semibold text-[var(--text-primary)]">
                {total > 0 ? "That's everything — thank you." : 'Nothing waiting for you right now.'}
              </p>
              <p className="mt-1 text-xs text-[var(--text-tertiary)]">
                {total > 0
                  ? "Param reviews every answer before anything changes, and you'll see each one marked Applied below when it's live."
                  : 'When a new decision needs your steer, it will appear here.'}
              </p>
            </div>
          ) : (
            <div className="px-5 py-4">
              {/* Counter + skip nav */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--text-tertiary)]">
                  Decision {idx + 1} of {total}
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-[var(--bg-overlay)] text-[var(--text-secondary)]">
                    {card.gap_id}
                  </span>
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setIdx((i) => Math.max(0, i - 1))}
                    disabled={idx === 0}
                    className="p-1 rounded-md text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] disabled:opacity-30 disabled:pointer-events-none transition-colors"
                    aria-label="Previous decision"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
                    disabled={idx >= total - 1}
                    className="p-1 rounded-md text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] disabled:opacity-30 disabled:pointer-events-none transition-colors"
                    aria-label="Next decision"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-[15px] font-semibold text-[var(--text-primary)] leading-snug">{card.title}</h3>

              {/* SPEC-327 — owner attribution + the one-week expectation. Shown to every
                  viewer of the card (already audience-scoped, so this leaks nothing). The
                  ranked fallback list and the provisional affordance are admin-only. */}
              {card.owner && (
                <p className="mt-2 flex items-start gap-1.5 text-[12px] text-[var(--text-secondary)] leading-snug">
                  <UserRound className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                  <span>
                    For: <span className="font-semibold text-[var(--text-primary)]">{cap(card.owner.expected)}</span>
                    {card.owner.why ? <span className="text-[var(--text-tertiary)]"> — {card.owner.why}</span> : null}
                  </span>
                </p>
              )}
              {/* SPEC-584 — the card was passed to this person; say by whom, with their note. */}
              {card.rerouted_from && (
                <p className="mt-2 inline-flex items-start gap-1.5 text-[12px] px-2 py-1 rounded-lg bg-sky-500/10 border border-sky-500/25 text-[var(--text-secondary)] leading-snug">
                  <Forward className="w-3.5 h-3.5 text-sky-400 mt-0.5 shrink-0" />
                  <span>
                    Passed to you by{' '}
                    <span className="font-semibold text-[var(--text-primary)]">{card.rerouted_from.display_name}</span>
                    {formatWhen(card.rerouted_from.at) ? ` on ${formatWhen(card.rerouted_from.at)}` : ''}
                    {card.rerouted_from.note ? <span className="text-[var(--text-tertiary)]"> — “{card.rerouted_from.note}”</span> : null}
                  </span>
                </p>
              )}
              {card.answer_by && (
                <div className="mt-2">
                  {card.is_overdue ? (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                      <Clock className="w-3 h-3" />
                      {isAdmin
                        ? `awaiting ${daysOverdue(card.answer_by)} day${daysOverdue(card.answer_by) === 1 ? '' : 's'} — provisional decision available`
                        : 'awaiting your response'}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-[var(--bg-overlay)] text-[var(--text-tertiary)] border border-[var(--border-subtle)]">
                      <Clock className="w-3 h-3" />
                      for answer by {formatWhen(card.answer_by)}
                    </span>
                  )}
                </div>
              )}
              {isAdmin && card.owner?.ranking && card.owner.ranking.length > 0 && (
                <div className="mt-2 rounded-lg border border-dashed border-[var(--border-subtle)] px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                    Who should answer — admin view
                  </p>
                  <ol className="mt-1 space-y-0.5">
                    {card.owner.ranking.map((r, i) => (
                      <li key={r.user} className="text-[12px] text-[var(--text-secondary)] leading-snug">
                        <span className="font-semibold text-[var(--text-primary)]">
                          {i + 1}. {cap(r.user)}
                        </span>
                        {r.why ? ` — ${r.why}` : ''}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* What this is */}
              <p className="mt-2 text-[13px] text-[var(--text-secondary)] leading-relaxed">{card.what_this_is}</p>

              {/* Why we're asking */}
              <div className="mt-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                  Why we're asking
                </p>
                <p className="mt-1 text-[13px] text-[var(--text-secondary)] leading-relaxed">{card.why_asking}</p>
              </div>

              {/* What runs today */}
              <div className="mt-3 rounded-lg bg-[var(--bg-overlay)] border border-[var(--border-subtle)] px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                  What runs today
                </p>
                <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">{card.running_today}</p>
              </div>

              {/* SPEC-327 — provisional decision (owner view). Param recorded an interim call
                  because the week lapsed; the normal answer flow below IS the revise action. */}
              {card.provisional && (
                <div className="mt-3 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2.5">
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-300">
                    <Gauge className="w-3.5 h-3.5" />
                    Decided provisionally
                  </p>
                  <p className="mt-1 text-[13px] text-[var(--text-secondary)] leading-relaxed">
                    {card.provisional.decision}
                    {card.provisional.rationale ? (
                      <span className="text-[var(--text-tertiary)]"> — {card.provisional.rationale}</span>
                    ) : null}
                  </p>
                  <p className="mt-1 text-[12px] text-[var(--text-tertiary)]">
                    You can revise this — answer below and your answer replaces the provisional call.
                  </p>
                </div>
              )}

              {/* SPEC-224 — peer answer (multi-person audiences). Shown as context whenever a
                  colleague has already answered; the agree/differ choice sits in the actions. */}
              {peer && (
                <div className="mt-4 rounded-lg bg-[var(--bg-overlay)] border border-[var(--border-subtle)] px-3 py-2.5">
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                    <Users className="w-3.5 h-3.5" />
                    {cap(peer.author)} already answered this
                    {formatWhen(peer.answered_at) ? ` on ${formatWhen(peer.answered_at)}` : ''}
                  </p>
                  <p className="mt-1 text-[13px] text-[var(--text-secondary)] leading-relaxed">
                    {peer.option_label ? (
                      <>
                        Their choice: <span className="font-semibold text-[var(--text-primary)]">{peer.option_label}</span>
                        {peer.answer_text ? ` — “${peer.answer_text}”` : ''}
                      </>
                    ) : peer.answer_text ? (
                      <>“{peer.answer_text}”</>
                    ) : (
                      <>They recorded an answer.</>
                    )}
                  </p>
                </div>
              )}

              {/* Options */}
              {card.options.length > 0 && !inChooseMode && (
                <div className="mt-4 space-y-2">
                  {card.options.map((opt) => {
                    const active = selected[card.gap_id] === opt.key;
                    return (
                      <button
                        key={opt.key}
                        onClick={() => setSelected((s) => ({ ...s, [card.gap_id]: opt.key }))}
                        disabled={isRecorded}
                        className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors ${
                          active
                            ? 'border-amber-500/60 bg-amber-500/10'
                            : 'border-[var(--border-subtle)] hover:border-amber-500/30 hover:bg-[var(--bg-overlay)]'
                        } ${isRecorded ? 'opacity-60 pointer-events-none' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                              active ? 'border-amber-400' : 'border-[var(--text-tertiary)]'
                            }`}
                          >
                            {active && <span className="w-2 h-2 rounded-full bg-amber-400" />}
                          </span>
                          <span className="text-[13px] font-semibold text-[var(--text-primary)]">{opt.label}</span>
                          {opt.recommended && (
                            <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/25 shrink-0">
                              our recommendation
                            </span>
                          )}
                        </div>
                        {opt.detail && (
                          <p className="mt-1 ml-6 text-[12px] text-[var(--text-tertiary)] leading-snug">{opt.detail}</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Free text — ALWAYS available alongside a choice (13 Aug, Param/Sudhin ask:
                  the answerer must be able to explain their decision on every card).
                  `allow_free_text` still governs whether free text ALONE is a valid answer;
                  it no longer hides the note box on option cards (G48/G49 were authored
                  without it and Sudhin lost the "Anything to add?" box on exactly those). */}
              {!inChooseMode && (
                <div className="mt-3">
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                    Anything to add?
                  </label>
                  <textarea
                    value={freeText[card.gap_id] || ''}
                    onChange={(e) => setFreeText((f) => ({ ...f, [card.gap_id]: e.target.value }))}
                    disabled={isRecorded}
                    rows={2}
                    placeholder="Optional — your own words, or use this on its own if no option fits."
                    className="mt-1 w-full rounded-lg bg-[var(--bg-base)] border border-[var(--border-subtle)] px-3 py-2 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-amber-500/50 resize-none disabled:opacity-60"
                  />
                </div>
              )}

              {/* SPEC-327 — admin-only provisional-decision affordance on an overdue card.
                  The suggestion is pre-filled from what the system runs today (the interim
                  default) and is fully editable; recording changes nothing and the owner can
                  still revise by answering. */}
              {isAdmin && card.is_overdue && !isRecorded && (
                <div className="mt-3 rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2.5">
                  {!provOpen[card.gap_id] ? (
                    <button
                      onClick={() => {
                        setProvOpen((s) => ({ ...s, [card.gap_id]: true }));
                        setProvDecision((s) => ({
                          ...s,
                          [card.gap_id]: s[card.gap_id] ?? (card.provisional?.decision || card.running_today || ''),
                        }));
                        setProvRationale((s) => ({
                          ...s,
                          [card.gap_id]: s[card.gap_id] ?? (card.provisional?.rationale || ''),
                        }));
                      }}
                      className="flex items-center gap-1.5 text-[12px] font-semibold text-amber-300 hover:text-amber-200 transition-colors"
                    >
                      <Gauge className="w-4 h-4" />
                      {card.provisional ? 'Update the provisional decision' : 'Record a provisional decision'}
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                        Provisional decision — pre-filled from what the system runs today; edit as the evidence warrants
                      </p>
                      <textarea
                        value={provDecision[card.gap_id] || ''}
                        onChange={(e) => setProvDecision((s) => ({ ...s, [card.gap_id]: e.target.value }))}
                        rows={2}
                        placeholder="The interim call to record"
                        className="w-full rounded-lg bg-[var(--bg-base)] border border-[var(--border-subtle)] px-3 py-2 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-amber-500/50 resize-none"
                      />
                      <textarea
                        value={provRationale[card.gap_id] || ''}
                        onChange={(e) => setProvRationale((s) => ({ ...s, [card.gap_id]: e.target.value }))}
                        rows={2}
                        placeholder="Why — the reasoning the owner will see (optional)"
                        className="w-full rounded-lg bg-[var(--bg-base)] border border-[var(--border-subtle)] px-3 py-2 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-amber-500/50 resize-none"
                      />
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setProvOpen((s) => ({ ...s, [card.gap_id]: false }))}
                          className="px-3 py-1.5 rounded-lg text-[12px] font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={onRecordProvisional}
                          disabled={provSubmitting || !(provDecision[card.gap_id] || '').trim()}
                          className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-amber-500 text-[var(--text-inverse)] hover:bg-amber-400 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                        >
                          {provSubmitting ? 'Recording…' : 'Record provisional decision'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* SPEC-584 — "Not my decision": pass it to the person who owns it. Recorded only;
                  Param updates the record. Every control is a native button, select, textarea or
                  checkbox, so the panel is keyboard reachable in tab order. */}
              {!isRecorded && (
                <div className="mt-3">
                  {!rrOpen[card.gap_id] ? (
                    <button
                      ref={rrButtonRef}
                      type="button"
                      onClick={openReroute}
                      className="flex items-center gap-1.5 text-[12px] font-medium text-[var(--text-tertiary)] hover:text-sky-300 transition-colors"
                    >
                      <Forward className="w-3.5 h-3.5" />
                      Not my decision
                    </button>
                  ) : (
                    <div className="rounded-lg border border-sky-500/25 bg-sky-500/5 px-3 py-2.5 space-y-2">
                      {card.reroute_suggestion ? (
                        <p className="text-[12px] text-[var(--text-secondary)] leading-snug">
                          We think this belongs to{' '}
                          <span className="font-semibold text-[var(--text-primary)]">{card.reroute_suggestion.display_name}</span>
                          {card.reroute_suggestion.why ? ` — ${card.reroute_suggestion.why}` : ''}
                        </p>
                      ) : (
                        <p className="text-[12px] text-[var(--text-secondary)]">Choose who this belongs to.</p>
                      )}
                      <div>
                        <label htmlFor={`rr-to-${card.gap_id}`} className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                          Pass it to
                        </label>
                        <select
                          ref={rrSelectRef}
                          id={`rr-to-${card.gap_id}`}
                          value={rrTo[card.gap_id] || ''}
                          onChange={(e) => setRrTo((s) => ({ ...s, [card.gap_id]: e.target.value }))}
                          disabled={people === null}
                          aria-busy={people === null && !peopleError}
                          className="mt-1 w-full rounded-lg bg-[var(--bg-base)] border border-[var(--border-subtle)] px-2 py-1.5 text-[13px] text-[var(--text-primary)] focus:outline-none focus:border-sky-500/50"
                        >
                          <option value="">
                            {people === null ? (peopleError ? 'List not loaded' : 'Loading people…') : 'Choose a person'}
                          </option>
                          {(people || []).map((p) => (
                            <option key={p.username} value={p.username}>
                              {p.display_name}
                              {card.reroute_suggestion?.username === p.username ? ' (suggested)' : ''}
                            </option>
                          ))}
                        </select>
                        {peopleError && (
                          <p className="mt-1 text-[11px] text-amber-300">
                            The list of people could not be loaded just now. Select Cancel, then Not my decision, to try again.
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor={`rr-note-${card.gap_id}`} className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                          A note for them
                        </label>
                        <textarea
                          id={`rr-note-${card.gap_id}`}
                          value={rrNote[card.gap_id] || ''}
                          onChange={(e) => setRrNote((s) => ({ ...s, [card.gap_id]: e.target.value }))}
                          rows={2}
                          maxLength={1000}
                          placeholder="Optional — why it is theirs, or who else to ask."
                          className="mt-1 w-full rounded-lg bg-[var(--bg-base)] border border-[var(--border-subtle)] px-3 py-2 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-sky-500/50 resize-none"
                        />
                      </div>
                      <label className="flex items-center gap-2 text-[12px] text-[var(--text-secondary)]">
                        <input
                          type="checkbox"
                          checked={!!rrKeep[card.gap_id]}
                          onChange={(e) => setRrKeep((s) => ({ ...s, [card.gap_id]: e.target.checked }))}
                        />
                        Keep it on my list too
                      </label>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            rrRefocusButton.current = true;
                            setRrOpen((s) => ({ ...s, [card.gap_id]: false }));
                          }}
                          className="px-3 py-1.5 rounded-lg text-[12px] font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={onReroute}
                          disabled={
                            rrSubmitting ||
                            !rrTo[card.gap_id] ||
                            // Only a person the list shows can be chosen (review finding 13).
                            !(people || []).some((p) => p.username === rrTo[card.gap_id])
                          }
                          className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-sky-500 text-[var(--text-inverse)] hover:bg-sky-400 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                        >
                          {rrSubmitting ? 'Passing it on…' : 'Pass it on'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {error && <p className="mt-3 text-[12px] text-red-400">{error}</p>}

              {/* Actions */}
              {isRecorded ? (
                <div className="mt-4">
                  <div className="rounded-lg bg-green-500/10 border border-green-500/25 px-3 py-2 flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    {passedTo !== undefined ? (
                      <p className="text-[12px] text-[var(--text-secondary)]">
                        Passed to <span className="font-semibold text-[var(--text-primary)]">{passedTo}</span>
                        {rrKeep[card.gap_id] ? ' — it stays on your list too' : ''}. Param sees it as well and
                        updates the record.
                      </p>
                    ) : (
                      <p className="text-[12px] text-[var(--text-secondary)]">
                        Recorded — Param reviews every answer before anything changes, and you'll see it marked{' '}
                        <span className="font-semibold text-[var(--text-primary)]">Applied</span> here when it's live.
                      </p>
                    )}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={goNextUnrecorded}
                      className="px-3 py-2 rounded-lg text-[13px] font-semibold bg-amber-500 text-[var(--text-inverse)] hover:bg-amber-400 transition-colors"
                    >
                      {queue.filter((c) => !isDone(c.gap_id)).length > 0 ? 'Next decision →' : 'Done'}
                    </button>
                  </div>
                </div>
              ) : inChooseMode ? (
                /* SPEC-224 — one-tap agree, or open the full flow to give your own view. */
                <div className="mt-4 space-y-3">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={onAgree}
                      disabled={submitting}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-[13px] font-semibold bg-amber-500 text-[var(--text-inverse)] hover:bg-amber-400 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                    >
                      <Check className="w-4 h-4" />
                      {submitting ? 'Recording…' : 'I agree'}
                    </button>
                    <button
                      onClick={() => setDiffer((d) => ({ ...d, [card.gap_id]: true }))}
                      disabled={submitting}
                      className="flex-1 px-3 py-2.5 rounded-lg text-[13px] font-semibold border border-[var(--border-subtle)] text-[var(--text-primary)] hover:border-amber-500/40 hover:bg-[var(--bg-overlay)] disabled:opacity-40 disabled:pointer-events-none transition-colors"
                    >
                      I see it differently
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <button
                      onClick={() => talkThrough(card)}
                      className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[13px] font-medium text-[var(--text-secondary)] hover:text-amber-400 hover:bg-[var(--bg-overlay)] transition-colors"
                    >
                      <MessageCircleQuestion className="w-4 h-4" />
                      Talk it through
                    </button>
                    <button
                      onClick={snooze}
                      className="px-3 py-2 rounded-lg text-[13px] font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors"
                    >
                      Later
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 flex items-center justify-between gap-2">
                  <button
                    onClick={() => talkThrough(card)}
                    className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[13px] font-medium text-[var(--text-secondary)] hover:text-amber-400 hover:bg-[var(--bg-overlay)] transition-colors"
                  >
                    <MessageCircleQuestion className="w-4 h-4" />
                    Talk it through
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={snooze}
                      className="px-3 py-2 rounded-lg text-[13px] font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors"
                    >
                      Later
                    </button>
                    <button
                      onClick={onSubmit}
                      disabled={!canSubmit}
                      className="px-3 py-2 rounded-lg text-[13px] font-semibold bg-amber-500 text-[var(--text-inverse)] hover:bg-amber-400 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                    >
                      {submitting ? 'Recording…' : 'Submit answer'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Your recent answers */}
          {mine.length > 0 && (
            <div className="border-t border-[var(--border-subtle)]">
              <button
                onClick={() => setShowRecent((v) => !v)}
                className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-[var(--bg-overlay)] transition-colors"
              >
                <span className="text-[12px] font-semibold text-[var(--text-secondary)]">
                  Your recent answers ({mine.length})
                </span>
                <ChevronRight
                  className={`w-4 h-4 text-[var(--text-tertiary)] transition-transform ${showRecent ? 'rotate-90' : ''}`}
                />
              </button>
              {showRecent && (
                <div className="px-5 pb-4 space-y-2">
                  {mine.map((m) => (
                    <div
                      key={m.gap_id}
                      className="flex items-start gap-2 rounded-lg border border-[var(--border-subtle)] px-3 py-2"
                    >
                      <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-0.5 shrink-0">
                        {m.gap_id}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[12px] text-[var(--text-primary)] leading-snug">{m.title}</p>
                        <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">
                          {m.option_label ? `You chose: ${m.option_label}` : null}
                          {m.option_label && m.answer_text ? ' · ' : null}
                          {m.answer_text ? `“${m.answer_text}”` : null}
                        </p>
                      </div>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${
                          m.chip === 'Applied'
                            ? 'bg-green-500/15 text-green-400 border border-green-500/25'
                            : 'bg-[var(--bg-overlay)] text-[var(--text-tertiary)] border border-[var(--border-subtle)]'
                        }`}
                      >
                        {m.chip}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
